import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError, API } from "../lib/api";
import { Upload, CheckCircle2, AlertCircle } from "lucide-react";
import MetaAutofillBanner from "../components/MetaAutofillBanner";
import { readAudioMetadata } from "../lib/mp3meta";
import BatchUploadReview from "../components/BatchUploadReview";
import { extractZipBatch } from "../lib/zipBatch";
import TagChipsInput from "../components/TagChipsInput";
import { MAIN_GENRES, STYLE_TAG_SUGGESTIONS, MOOD_TAG_SUGGESTIONS } from "../lib/taxonomy";

const CATEGORIES = [
  { id: "music", label: "Music" },
  { id: "worship", label: "Worship" },
  { id: "instrumental", label: "Instrumental" },
  { id: "sermon", label: "Sermon" },
  { id: "teaching", label: "Teaching" },
  { id: "study", label: "Study song" },
];

export default function SubmitPage() {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    title: "",
    artist: "",
    genre: "",
    main_genre: "",
    description: "",
    lyrics: "",
    scripture_theme: "",
    category: "music",
    owns_rights: false,
  });
  const [styleTags, setStyleTags] = useState([]);
  const [moodTags, setMoodTags] = useState([]);
  const [popularStyle, setPopularStyle] = useState([]);
  const [popularMood, setPopularMood] = useState([]);
  useEffect(() => {
    api.get("/taxonomy/popular-tags?kind=style").then((r) => setPopularStyle(r.data?.tags || [])).catch(() => {});
    api.get("/taxonomy/popular-tags?kind=mood").then((r) => setPopularMood(r.data?.tags || [])).catch(() => {});
  }, []);
  const [audio, setAudio] = useState(null);
  const [cover, setCover] = useState(null);
  const [coverVideo, setCoverVideo] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [progress, setProgress] = useState(0);
  const [audioMeta, setAudioMeta] = useState(null);
  const [autofilled, setAutofilled] = useState(new Set());
  const [coverApplied, setCoverApplied] = useState(false);
  // Batch (ZIP) state
  const [batchRows, setBatchRows] = useState(null); // null when not in batch mode
  const [batchErrors, setBatchErrors] = useState([]);
  const [batchSuccessCount, setBatchSuccessCount] = useState(0);

  const onChange = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const onCheck = (k) => (e) => setForm({ ...form, [k]: e.target.checked });

  const isZipFile = (f) => !!f && (f.name?.toLowerCase().endsWith(".zip") || f.type === "application/zip" || f.type === "application/x-zip-compressed");

  const onAudioChange = async (file) => {
    setAudio(file);
    setAudioMeta(null);
    setAutofilled(new Set());
    setCoverApplied(false);
    setBatchRows(null);
    setBatchErrors([]);
    setError("");
    if (!file) return;
    if (isZipFile(file)) {
      // Switch to batch mode
      const res = await extractZipBatch(file);
      if (res.summary && res.rows.length > 0) {
        setBatchRows(res.rows);
        setBatchErrors(res.errors);
      } else {
        setError(res.errors?.[0] || "Could not read ZIP file.");
        setAudio(null);
      }
      return;
    }
    const meta = await readAudioMetadata(file);
    setAudioMeta(meta);
  };

  const onAutofillFromMeta = (subset) => {
    if (!subset || Object.keys(subset).length === 0) return;
    setForm((f) => ({ ...f, ...subset }));
    setAutofilled(new Set(Object.keys(subset)));
  };

  const onUseEmbeddedCover = (file) => {
    setCover(file);
    setCoverApplied(true);
  };

  // Per-row uploader for ZIP batch — posts to /submissions
  const uploadOneSubmission = async (row) => {
    if (!form.owns_rights) {
      return { ok: false, error: "Please check the rights confirmation below before uploading." };
    }
    const fd = new FormData();
    fd.append("title", row.title.trim());
    fd.append("artist", row.artist.trim());
    fd.append("category", form.category || "music");
    fd.append("owns_rights", "true");
    if (row.genre) fd.append("genre", row.genre);
    if (row.main_genre) fd.append("main_genre", row.main_genre);
    if (row.album) fd.append("description", `From album: ${row.album}`);
    if (row.lyrics) fd.append("lyrics", row.lyrics);
    fd.append("audio", row.audioFile);
    if (row.coverFile) fd.append("cover", row.coverFile);
    try {
      const r = await api.post("/submissions", fd);
      // eslint-disable-next-line no-console
      console.info("[Batch submission OK]", { id: r?.data?.id, title: row.title, artist: row.artist });
      return { ok: true };
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error("[Batch submission failed]", { title: row.title, artist: row.artist, status: e?.response?.status, message: e?.message });
      return { ok: false, error: formatApiError(e) };
    }
  };

  const onBatchAllDone = () => {
    const rows = batchRows || [];
    const succeeded = rows.filter((_r, i) => true).length; // we don't track per-row final state here, parent handles in BatchUploadReview
    setBatchSuccessCount(succeeded);
    // Stay on batch screen so user can see per-row status; offer "Done" via the submit-success state
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!audio) { setError("Please choose an audio file."); return; }
    if (!form.owns_rights) { setError("You must confirm you own or have permission to upload this audio."); return; }

    const fd = new FormData();
    fd.append("title", form.title);
    fd.append("artist", form.artist);
    fd.append("category", form.category);
    fd.append("owns_rights", "true");
    if (form.genre) fd.append("genre", form.genre);
    if (form.main_genre) fd.append("main_genre", form.main_genre);
    if (styleTags.length) fd.append("style_tags", styleTags.join(","));
    if (moodTags.length) fd.append("mood_tags", moodTags.join(","));
    if (form.description) fd.append("description", form.description);
    if (form.lyrics) fd.append("lyrics", form.lyrics);
    if (form.scripture_theme) fd.append("scripture_theme", form.scripture_theme);
    fd.append("audio", audio);
    if (cover) fd.append("cover", cover);
    if (coverVideo) fd.append("cover_video", coverVideo);

    setSubmitting(true);
    setProgress(0);
    const targetUrl = `${API}/submissions`;
    const submitToast = toast.loading("Uploading your submission…", { id: "submit-toast" });
    try {
      // NOTE: do NOT manually set Content-Type for multipart/form-data — axios needs to set the boundary itself.
      const res = await api.post("/submissions", fd, {
        onUploadProgress: (evt) => {
          if (evt.total) setProgress(Math.round((evt.loaded / evt.total) * 100));
        },
      });
      // eslint-disable-next-line no-console
      console.info("[Submission OK]", { id: res?.data?.id, title: form.title, artist: form.artist });
      toast.success("Submitted for admin review", {
        id: "submit-toast",
        description: `"${form.title}" — ${form.artist}. We'll notify the team.`,
      });
      setSuccess(true);
    } catch (err) {
      const status = err?.response?.status;
      const body = err?.response?.data;
      // eslint-disable-next-line no-console
      console.error("[Submission failed]", { url: targetUrl, status, body, message: err?.message });
      let msg = formatApiError(err);
      if (status === 404) {
        msg = `Submission endpoint not reachable (404). The frontend tried: ${targetUrl}. Check that REACT_APP_BACKEND_URL is set to a backend that exposes /api/submissions.`;
      } else if (status === 401 || status === 403) {
        msg = `${msg || "Not authorized"} — your session may have expired. Please sign out and sign back in.`;
      } else if (status === 413) {
        msg = "File is too large for the server. Try a smaller audio file (≤ 120 MB) or compress the cover art.";
      } else if (status === 500) {
        msg = `${msg || "Submission failed during upload."} Please try again. If it keeps failing, contact the admin — your attempt was logged.`;
      } else if (!status) {
        msg = `Network error reaching ${targetUrl}. Check your connection or the backend status.`;
      }
      setError(msg);
      toast.error("Submission failed", {
        id: "submit-toast",
        description: msg.length > 220 ? `${msg.slice(0, 220)}…` : msg,
        duration: 8000,
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <div className="max-w-xl mx-auto my-16 text-center">
        <div className="card-surface p-12">
          <CheckCircle2 className="w-14 h-14 text-[#C5A059] mx-auto mb-4" strokeWidth={1.5} />
          <h1 className="font-serif text-3xl text-[#FDFBF7] tracking-tight mb-3">Submitted for review</h1>
          <p className="text-[#A1A4AB]">Thank you for sharing. An admin will review your submission soon. You'll see it appear on the station once approved.</p>
          <div className="flex justify-center gap-3 mt-8 flex-wrap">
            <button onClick={() => { setSuccess(false); setForm({ title: "", artist: "", genre: "", description: "", lyrics: "", scripture_theme: "", category: "music", owns_rights: false }); setAudio(null); setCover(null); setCoverVideo(null); setProgress(0); setAudioMeta(null); setAutofilled(new Set()); setCoverApplied(false); }} className="btn-secondary" data-testid="submit-another-btn">
              Submit another
            </button>
            <button onClick={() => navigate("/my-submissions")} className="btn-primary" data-testid="view-my-submissions-btn">
              View my submissions
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto">
      <div className="mb-8">
        <div className="overline text-[#C5A059] mb-2">Contribute to the station</div>
        <h1 className="font-serif text-4xl sm:text-5xl text-[#FDFBF7] tracking-tight">Submit your music</h1>
        <p className="text-[#A1A4AB] mt-3 max-w-2xl">
          Songs, sermons, and teachings submitted here are reviewed by the admin before appearing publicly. Every uploader confirms they have the right to share this audio.
        </p>
      </div>

      <form onSubmit={submit} className="card-surface p-8 md:p-10 space-y-6" data-testid="submit-form">
        {/* Single-track form fields hidden in batch mode (a ZIP was selected) */}
        {!batchRows && (
        <>
        <div className="grid md:grid-cols-2 gap-5">
          <div>
            <label className="text-sm text-[#A1A4AB] mb-2 block">Song title *</label>
            <input required={!batchRows} className="input-lux" value={form.title} onChange={onChange("title")} data-testid="submit-title-input" />
          </div>
          <div>
            <label className="text-sm text-[#A1A4AB] mb-2 block">Artist / group name *</label>
            <input required={!batchRows} className="input-lux" value={form.artist} onChange={onChange("artist")} data-testid="submit-artist-input" />
          </div>
          <div>
            <label className="text-sm text-[#A1A4AB] mb-2 block">Main genre</label>
            <select className="input-lux" value={form.main_genre} onChange={onChange("main_genre")} data-testid="submit-main-genre-select">
              <option value="">Auto-map from genre text</option>
              {MAIN_GENRES.map((g) => <option key={g} value={g}>{g}</option>)}
            </select>
          </div>
          <div>
            <label className="text-sm text-[#A1A4AB] mb-2 block">Genre (free text)</label>
            <input className="input-lux" value={form.genre} onChange={onChange("genre")} placeholder="e.g. Worship, Gospel, Boom bap" data-testid="submit-genre-input" />
          </div>
          <div>
            <label className="text-sm text-[#A1A4AB] mb-2 block">Category *</label>
            <select className="input-lux" value={form.category} onChange={onChange("category")} data-testid="submit-category-select">
              {CATEGORIES.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
            </select>
          </div>
        </div>

        <TagChipsInput
          label="Style / subgenre tags (optional)"
          value={styleTags}
          onChange={setStyleTags}
          suggestions={STYLE_TAG_SUGGESTIONS}
          popularTags={popularStyle}
          placeholder="e.g. Lo-fi, Boom Bap, Acoustic — Enter to add"
          testId="submit-style-tags"
        />
        <TagChipsInput
          label="Mood tags (optional)"
          value={moodTags}
          onChange={setMoodTags}
          suggestions={MOOD_TAG_SUGGESTIONS}
          popularTags={popularMood}
          placeholder="e.g. Reflective, Prayerful, Energetic — Enter to add"
          testId="submit-mood-tags"
          maxItems={10}
        />

        <div>
          <label className="text-sm text-[#A1A4AB] mb-2 block">Description</label>
          <textarea className="input-lux" value={form.description} onChange={onChange("description")} placeholder="What's this song or teaching about?" data-testid="submit-description-input" />
        </div>

        <div className="grid md:grid-cols-2 gap-5">
          <div>
            <label className="text-sm text-[#A1A4AB] mb-2 block">Lyrics (optional)</label>
            <textarea className="input-lux" value={form.lyrics} onChange={onChange("lyrics")} data-testid="submit-lyrics-input" />
          </div>
          <div>
            <label className="text-sm text-[#A1A4AB] mb-2 block">Scripture / study theme (optional)</label>
            <textarea className="input-lux" value={form.scripture_theme} onChange={onChange("scripture_theme")} placeholder="e.g. Psalm 23, study of Revelation" data-testid="submit-scripture-input" />
          </div>
        </div>
        </>
        )}

        <div className="grid md:grid-cols-2 gap-5">
          <div>
            <label className="text-sm text-[#A1A4AB] mb-2 block">Audio file * (MP3, MP4, WAV, M4A, OGG, FLAC — or a .ZIP of MP3s for batch upload — max 120 MB single / 600 MB ZIP)</label>
            <input
              type="file"
              accept=".mp3,.mp4,.wav,.m4a,.aac,.ogg,.flac,.webm,.zip,audio/*,application/zip"
              onChange={(e) => onAudioChange(e.target.files?.[0] || null)}
              className="block w-full text-sm text-[#A1A4AB] file:mr-3 file:py-2.5 file:px-4 file:rounded-full file:border-0 file:bg-[#C5A059] file:text-[#060709] file:font-semibold hover:file:bg-[#DCC286] file:cursor-pointer"
              data-testid="submit-audio-input"
            />
            {audio && <div className="text-xs text-[#6C707A] mt-2">{audio.name} — {(audio.size / 1024 / 1024).toFixed(2)} MB{batchRows && " · ZIP batch mode"}</div>}
            {audio && audioMeta !== null && !batchRows && (
              <div className="mt-3">
                <MetaAutofillBanner
                  meta={audioMeta}
                  formValues={form}
                  onAutofill={onAutofillFromMeta}
                  onUseCover={onUseEmbeddedCover}
                  hasUserCover={!!cover && !coverApplied}
                  appliedFields={autofilled}
                  appliedCover={coverApplied}
                />
              </div>
            )}
          </div>
          {!batchRows && (
          <div>
            <label className="text-sm text-[#A1A4AB] mb-2 block">Cover image (optional — JPG/PNG/WEBP, max 6 MB)</label>
            <input
              type="file"
              accept="image/jpeg,image/png,image/webp"
              onChange={(e) => setCover(e.target.files?.[0] || null)}
              className="block w-full text-sm text-[#A1A4AB] file:mr-3 file:py-2.5 file:px-4 file:rounded-full file:border-0 file:bg-[#1A1D24] file:text-[#FDFBF7] file:border file:border-[#22262E] hover:file:border-[#C5A059] file:cursor-pointer"
              data-testid="submit-cover-input"
            />
            {cover && <div className="text-xs text-[#6C707A] mt-2">{cover.name}</div>}
          </div>
          )}
        </div>

        {!batchRows && (
        <div>
          <label className="text-sm text-[#A1A4AB] mb-2 block">Animated cover (optional — Suno MP4 / WEBM, max 60 MB)</label>
          <p className="text-xs text-[#6C707A] mb-2">If your song has a Suno-style animated cover video, upload it here. It will play muted and looped as moving cover art while your song plays. Leave empty for a static cover.</p>
          <input
            type="file"
            accept="video/mp4,video/webm,.mp4,.webm"
            onChange={(e) => setCoverVideo(e.target.files?.[0] || null)}
            className="block w-full text-sm text-[#A1A4AB] file:mr-3 file:py-2.5 file:px-4 file:rounded-full file:border-0 file:bg-[#1A1D24] file:text-[#FDFBF7] file:border file:border-[#22262E] hover:file:border-[#C5A059] file:cursor-pointer"
            data-testid="submit-cover-video-input"
          />
          {coverVideo && <div className="text-xs text-[#6C707A] mt-2">{coverVideo.name} — {(coverVideo.size / 1024 / 1024).toFixed(2)} MB</div>}
        </div>
        )}

        {/* Batch (ZIP) review screen — replaces single-song fields above */}
        {batchRows && (
          <BatchUploadReview
            rows={batchRows}
            setRows={setBatchRows}
            errors={batchErrors}
            mode="public"
            onCancel={() => { setBatchRows(null); setAudio(null); setBatchErrors([]); }}
            uploader={uploadOneSubmission}
            onAllDone={onBatchAllDone}
          />
        )}

        <div className="flex items-start gap-3 p-4 rounded-md border border-[#22262E] bg-[#111317]">
          <input
            type="checkbox"
            id="owns_rights"
            checked={form.owns_rights}
            onChange={onCheck("owns_rights")}
            className="mt-1 w-4 h-4 accent-[#C5A059]"
            data-testid="submit-rights-checkbox"
          />
          <label htmlFor="owns_rights" className="text-sm text-[#A1A4AB] leading-relaxed">
            I confirm that I own this recording (or every recording in this ZIP) or have explicit permission to share it on Yah-Wave Radio. I understand submissions are reviewed by an admin before appearing publicly, and approved submissions are stream-only (not available for download in this version).
          </label>
        </div>

        {error && (
          <div className="flex items-start gap-2 text-red-400 text-sm" data-testid="submit-error">
            <AlertCircle className="w-4 h-4 mt-0.5" strokeWidth={1.5} /> {error}
          </div>
        )}

        {!batchRows && submitting && progress > 0 && (
          <div className="space-y-1">
            <div className="flex justify-between text-xs text-[#A1A4AB]">
              <span>Uploading…</span>
              <span>{progress}%</span>
            </div>
            <div className="h-1.5 bg-[#22262E] rounded-full overflow-hidden">
              <div className="h-full bg-[#C5A059] transition-all" style={{ width: `${progress}%` }} />
            </div>
          </div>
        )}

        {!batchRows && (
          <button type="submit" disabled={submitting} className="btn-primary w-full justify-center" data-testid="submit-submit-btn">
            <Upload className="w-4 h-4" strokeWidth={1.5} /> {submitting ? "Submitting…" : "Submit for review"}
          </button>
        )}
      </form>
    </div>
  );
}
